import React, { useLayoutEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { AuthProvider } from '@/contexts/AuthContext';
import ProtectedRoute from '@/components/ProtectedRoute';
import DashboardLayout from '@/components/DashboardLayout';
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import Orders from '@/pages/Orders';
import Kitchen from '@/pages/Kitchen';
import Customers from '@/pages/Customers';
import Menu from '@/pages/Menu';
import WhatsApp from '@/pages/WhatsApp';
import Reports from '@/pages/Reports';
import Settings from '@/pages/Settings';
import Companies from '@/pages/Companies';
import Restaurants from '@/pages/Restaurants';
import Users from '@/pages/Users';
import Subscriptions from '@/pages/Subscriptions';
import AIBotSettings from '@/pages/AIBotSettings';
import RestaurantOnboarding from '@/pages/RestaurantOnboarding';
import PublicMenu from '@/pages/PublicMenu';
import '@/i18n';
import '@/App.css';

const navigateDocument = url => window.location.replace(url);

export function DocumentPrivacyBoundary({ children, navigate = navigateDocument }) {
  const location = useLocation();
  let pathname;
  try { pathname = decodeURI(location.pathname); }
  catch (_) { pathname = location.pathname; }
  const targetMode = /^\/public-menu(?:\/|$)/i.test(pathname) ? 'public-menu' : 'ordinary';
  const documentMode = window.__UNIGATE_DOCUMENT_MODE__;
  // Missing/invalid bootstrap state is not evidence of an analytics-free document.
  const permitted = documentMode === targetMode;
  const target = location.pathname + location.search + location.hash;
  const requested = useRef(null);

  useLayoutEffect(() => {
    if (permitted) {
      requested.current = null;
      return;
    }
    if (requested.current === target) return;
    requested.current = target;
    try { navigate(target); }
    catch (_) { /* Keep children unmounted if navigation fails. */ }
  }, [permitted, target, navigate]);

  return permitted ? children : <main role="status">Opening page safely…</main>;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <DocumentPrivacyBoundary>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="orders" element={<Orders />} />
              <Route path="kitchen" element={<Kitchen />} />
              <Route path="customers" element={<Customers />} />
              <Route path="menu" element={<Menu />} />
              <Route path="whatsapp" element={<WhatsApp />} />
              <Route path="reports" element={<Reports />} />
              <Route path="settings" element={<Settings />} />
              <Route path="companies" element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <Companies />
                </ProtectedRoute>
              } />
              <Route path="restaurants" element={
                <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
                  <Restaurants />
                </ProtectedRoute>
              } />
              <Route path="users" element={
                <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
                  <Users />
                </ProtectedRoute>
              } />
              <Route path="subscriptions" element={
                <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
                  <Subscriptions />
                </ProtectedRoute>
              } />
              <Route path="onboarding/restaurant" element={
                <ProtectedRoute allowedRoles={['company_admin']}>
                  <RestaurantOnboarding />
                </ProtectedRoute>
              } />
            <Route path="ai-bot" element={
             <ProtectedRoute allowedRoles={['company_admin', 'restaurant_manager']}>
                <AIBotSettings />
               </ProtectedRoute>
             } />
             </Route>
           <Route path="public-menu/:restaurantId" element={<PublicMenu />} />
          </Routes>
          </DocumentPrivacyBoundary>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
